/**
 * Configuración de la aplicación
 * Aquí se definen las funcionalidades y secciones disponibles en la app
 */

export interface AppFeature {
  enabled: boolean;
  icon?: string;
  label?: string;
  order?: number;
}

export interface NetworkingField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  required: boolean;
  visible: boolean;
  order: number;
}

export type AuthenticationMode = 'cedula' | 'email_password';
export type AdministrativeSectionKey =
  | 'membership'
  | 'associationEvents'
  | 'participation'
  | 'accountStatement'
  | 'digitalCard'
  | 'benefits'
  | 'news'
  | 'certificates';

export interface AdministrativeSectionConfig extends AppFeature {
  route: string;
  description: string;
}

export interface AppConfig {
  // Funcionalidades principales
  features: {
    schedule: AppFeature;
    speakers: AppFeature;
    questions: AppFeature;
    favorites: AppFeature;
    calendar: AppFeature;
    badge: AppFeature;
    networking: AppFeature;
    map: AppFeature;
    about: AppFeature;
    carnetDigital: AppFeature & {
      mandatoryEventCode?: string; // Código del evento mandatorio para consultas del carnet digital
    };
  };

  // Configuración de autenticación
  auth: {
    mode?: AuthenticationMode; // Por ahora la app decide qué formulario utiliza
    allowAccountActivation?: boolean;
    multievent: boolean; // Permite login con múltiples eventos
    client: string; // ID del cliente por defecto
    defaultEventID?: string; // ID del evento por defecto cuando multievent es false
    defaultRoute: string; // Ruta a la que se redirige después de seleccionar un evento o cerrar la selección
    showPasswordAsText: boolean; // Si true, muestra la contraseña como texto visible; si false, como password oculto
  };

  // Configuración de networking
  networking: {
    enabled: boolean;
    qrCodePrefix: string; // Prefijo para identificar QRs de la app
    fields: NetworkingField[];
    maxScansPerUser: number;
    allowDuplicateScans: boolean;
  };

  // Configuración de tabs del menú inferior
  bottomTabs: {
    schedule: AppFeature;
    networking: AppFeature;
    speakers: AppFeature;
    badge: AppFeature;
  };

  // Configuración de preguntas
  questions: {
    enabled: boolean;
    allowAnonymous: boolean;
    requireModeration: boolean;
    maxQuestionsPerUser: number;
  };

  // Configuración de favoritos
  favorites: {
    enabled: boolean;
    syncWithFirebase: boolean;
    maxFavorites: number;
  };

  // Secciones administrativas opcionales para asociaciones y colegios.
  administrative: {
    enabled: boolean;
    organizationName: string;
    useDummyData: boolean;
    dataEndpoint?: string;
    sections: Record<AdministrativeSectionKey, AdministrativeSectionConfig>;
  };
}

// Configuración por defecto de la aplicación
export const defaultAppConfig: AppConfig = {
  features: {
    schedule: {
      enabled: true,
      icon: 'calendar-outline',
      label: 'Agenda',
      order: 1
    },
    questions: {
      enabled: true,
      icon: 'help-circle-outline',
      label: 'Preguntas',
      order: 3
    },
    favorites: {
      enabled: true,
      icon: 'heart-outline',
      label: 'Favoritos',
      order: 4
    },
    calendar: {
      enabled: true,
      icon: 'calendar',
      label: 'Calendario',
      order: 5
    },
    badge: {
      enabled: false,
      icon: 'card-outline',
      label: 'Gafete',
      order: 6
    },
    networking: {
      enabled: true,
      icon: 'people-circle-outline',
      label: 'Networking',
      order: 7
    },
     speakers: {
      enabled: true,
      icon: 'people-outline',
      label: 'Conferencistas',
      order: 2
    },
    map: {
      enabled: true,
      icon: 'map-outline',
      label: 'Mapa',
      order: 8
    },
    about: {
      enabled: true,
      icon: 'information-circle-outline',
      label: 'Acerca de',
      order: 9
    },
    carnetDigital: {
      enabled: false,
      icon: 'card-outline',
      label: 'Carnet Digital',
      order: 10,
      mandatoryEventCode: '89885FF2' // Código del evento base para consultas del carnet digital
    }
  },

  // Configuración de autenticación
  auth: {
    mode: 'email_password', // Cambiar a email_password para builds con acceso Firebase
    allowAccountActivation: true,
    multievent: false, // Se fuerza a true automáticamente cuando administrative.enabled está activo
    client: '8', // Cliente por defecto
    defaultEventID: '05546B0E', // Evento actual que se carga inicialmente; multievento conserva acceso al historial
    defaultRoute: '/tabs/schedule', // Ruta por defecto después de seleccionar evento (ej: '/tabs/schedule', '/tabs/speakers', '/account')
    showPasswordAsText: false // Por defecto, ocultar la contraseña (false = password, true = text)
  },
  
  bottomTabs: {
    schedule: {
      enabled: true,
      icon: 'calendar-outline',
      label: 'Agenda',
      order: 1
    },
    networking: {
      enabled: true,
      icon: 'people-circle-outline',
      label: 'Networking',
      order: 3
    },
    speakers: {
      enabled: true,
      icon: 'people-outline',
      label: 'Speakers',
      order: 2
    },
    badge: {
      enabled: false,
      icon: 'card-outline',
      label: 'Gafete',
      order: 4
    }
  },
  
  networking: {
    enabled: true,
    qrCodePrefix: 'ESIMPOSIO_USER_',
    maxScansPerUser: 500,
    allowDuplicateScans: false,
    fields: [
      {
        key: 'name',
        label: 'Nombre',
        type: 'text',
        required: true,
        visible: true,
        order: 1
      },
      {
        key: 'email',
        label: 'Correo Electrónico',
        type: 'email',
        required: true,
        visible: true,
        order: 2
      },
      {
        key: 'phone',
        label: 'Teléfono',
        type: 'phone',
        required: false,
        visible: true,
        order: 3
      },
      {
        key: 'company',
        label: 'Empresa',
        type: 'text',
        required: false,
        visible: true,
        order: 4
      },
      {
        key: 'position',
        label: 'Cargo',
        type: 'text',
        required: false,
        visible: true,
        order: 5
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        type: 'url',
        required: false,
        visible: false,
        order: 6
      }
    ]
  },
  
  questions: {
    enabled: true,
    allowAnonymous: false,
    requireModeration: true,
    maxQuestionsPerUser: 10
  },
  
  favorites: {
    enabled: true,
    syncWithFirebase: true,
    maxFavorites: 50
  },

  administrative: {
    enabled: false,
    organizationName: 'Asociación profesional',
    // Posteriormente puede cambiarse a false y configurar dataEndpoint.
    useDummyData: true,
    dataEndpoint: undefined,
    sections: {
      membership: {
        enabled: true,
        icon: 'person-circle-outline',
        label: 'Mi membresía',
        order: 1,
        route: '/administrative/membership',
        description: 'Estado y datos de afiliación'
      },
      associationEvents: {
        enabled: true,
        icon: 'calendar-outline',
        label: 'Eventos institucionales',
        order: 2,
        route: '/administrative/events',
        description: 'Actividades, historial y agenda'
      },
      participation: {
        enabled: true,
        icon: 'checkbox-outline',
        label: 'Participar',
        order: 3,
        route: '/administrative/participation',
        description: 'Votaciones, encuestas y convocatorias'
      },
      accountStatement: {
        enabled: true,
        icon: 'wallet-outline',
        label: 'Estado de cuenta',
        order: 4,
        route: '/administrative/account',
        description: 'Cuotas, pagos y movimientos'
      },
      digitalCard: {
        enabled: true,
        icon: 'card-outline',
        label: 'Carné de asociado',
        order: 5,
        route: '/administrative/card',
        description: 'Identificación y validación QR'
      },
      benefits: {
        enabled: true,
        icon: 'star-outline',
        label: 'Beneficios',
        order: 6,
        route: '/administrative/benefits',
        description: 'Convenios y descuentos'
      },
      news: {
        enabled: true,
        icon: 'notifications-outline',
        label: 'Noticias',
        order: 7,
        route: '/administrative/news',
        description: 'Comunicaciones institucionales'
      },
      certificates: {
        enabled: true,
        icon: 'ribbon-outline',
        label: 'Certificados',
        order: 8,
        route: '/administrative/certificates',
        description: 'Historial de formación'
      }
    }
  }
};

// Función para obtener la configuración (puede ser extendida para cargar desde servidor)
export const getAppConfig = (): AppConfig => {
  // En el futuro, esto puede cargar la configuración desde un servidor
  // Por ahora retorna la configuración por defecto
  return defaultAppConfig;
};

// Función para verificar si una funcionalidad está habilitada
export const isFeatureEnabled = (feature: keyof AppConfig['features']): boolean => {
  const config = getAppConfig();
  return config.features[feature]?.enabled || false;
};

// Función para obtener los campos de networking configurados
export const getNetworkingFields = (): NetworkingField[] => {
  const config = getAppConfig();
  return config.networking.fields
    .filter(field => field.visible)
    .sort((a, b) => a.order - b.order);
};

// Función para obtener los tabs del menú inferior habilitados
export const getEnabledBottomTabs = () => {
  const config = getAppConfig();
  return Object.entries(config.bottomTabs)
    .filter(([_, tab]) => tab.enabled)
    .sort(([_, a], [__, b]) => (a.order || 0) - (b.order || 0))
    .map(([key, tab]) => ({ key, ...tab }));
};

// Función para obtener la configuración de autenticación
export const getAuthConfig = () => {
  const config = getAppConfig();
  const multievent = config.administrative.enabled || config.auth.multievent;

  return {
    ...config.auth,
    multievent
  };
};

// Función para verificar si multievent está habilitado
export const isMultieventEnabled = (): boolean => {
  return getAuthConfig().multievent;
};

// Función para obtener el cliente por defecto
export const getDefaultClient = (): string => {
  const config = getAppConfig();
  return config.auth.client;
};

// Función para obtener el evento por defecto
export const getDefaultEventID = (): string | undefined => {
  return getAuthConfig().defaultEventID;
};

export const isAdministrativeSectionEnabled = (section: AdministrativeSectionKey): boolean => {
  const config = getAppConfig();
  return config.administrative.enabled && Boolean(config.administrative.sections[section]?.enabled);
};

export const getEnabledAdministrativeSections = () => {
  const config = getAppConfig();
  if (!config.administrative.enabled) return [];

  return Object.entries(config.administrative.sections)
    .filter(([_, section]) => section.enabled)
    .sort(([_, a], [__, b]) => (a.order || 0) - (b.order || 0))
    .map(([key, section]) => ({ key: key as AdministrativeSectionKey, ...section }));
};
